import { Table, Input } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../../../Config/Config";
import PdfExcelPrint from "../../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "GroupName", label: "Group Name" },
  { key: "QTY", label: "Qty", badge: "pieces" },
];
const secondaryFields = [
  { key: "DPrice", label: "DAmount" },
  { key: "CPrice", label: "CAmount" },
];

const GropuWiseStock = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchGroup, setSearchGroup] = useState("");
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CURRENT_STOCK`,
        {
          headers: {
            tenantName: tenantName,
          },
        }
      )
      .then((response) => {
        // filter out items whose Qty <= 0
        const detailsData = (response.data || []).filter(
          (item) => Number(item?.Qty ?? item?.QTY ?? 0) > 0
        );

        // Group by GroupName
        const map = new Map();

        detailsData.forEach((item) => {
          const groupName = item.GroupName || item.GROUPNAME || item.CompName || "UNKNOWN";

          if (!map.has(groupName)) {
            map.set(groupName, {
              GroupName: groupName,
              QTY: 0,
              DPrice: 0,
              CPrice: 0,
            });
          }

          const entry = map.get(groupName);
          const qty = Number(item.Qty ?? item.QTY ?? 0);
          const dPrice = Number(item.DPrice ?? item.DPRICE ?? 0);
          const cPrice = Number(item.CPrice ?? item.CPRICE ?? 0);

          entry.QTY += qty;
          entry.DPrice += qty * dPrice;
          entry.CPrice += qty * cPrice;
        });

        const calculatedData = Array.from(map.values()).map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));

        setData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching group wise stock:", error);
        setData([]);
        setTimeout(() => setTableVisible(true), 80);
      })
      .finally(() => setLoading(false));
  }, [tenantName]);

  useEffect(() => {
    let filtered = [...data];

    if (searchGroup) {
      const term = searchGroup.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.GroupName || "").toLowerCase().includes(term)
      );
    }

    setFilteredData(
      filtered.map((item, index) => ({
        ...item,
        serialNo: index + 1,
      }))
    );
  }, [data, searchGroup]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Group Name",
      dataIndex: "GroupName",
      key: "GroupName",
      align: "left",
      className: "col-tagno",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "QTY",
      dataIndex: "QTY",
      key: "QTY",
      align: "center",
      className: "col-pieces",
      render: (value) => <span className="piecesBadge">{value}</span>,
    },
    {
      title: "DAmount",
      dataIndex: "DPrice",
      key: "DPrice",
      align: "right",
      render: (value) => <span>{Number(value || 0).toFixed(2)}</span>,
    },
    {
      title: "CAmount",
      dataIndex: "CPrice",
      key: "CPrice",
      align: "right",
      className: "col-netwt",
      render: (value) => (
        <span className="wtBadge">{Number(value || 0).toFixed(2)}</span>
      ),
    },
  ];

  const getTotals = (data) => {
    return data.reduce(
      (acc, item) => {
        acc.QTY += Number(item.QTY || 0);
        acc.DPrice += Number(item.DPrice || 0);
        acc.CPrice += Number(item.CPrice || 0);
        return acc;
      },
      { QTY: 0, DPrice: 0, CPrice: 0 }
    );
  };

  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        DPrice: Number(item.DPrice || 0),
        CPrice: Number(item.CPrice || 0),
      })),
    [filteredData]
  );

  const totals = useMemo(() => getTotals(filteredData), [filteredData]);

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      GroupName: "",
      QTY: totals.QTY,
      DPrice: totals.DPrice.toFixed(2),
      CPrice: totals.CPrice.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="Group Wise Stock"
        subtitle="Inventory · Group Wise Stock"
        icon={<AppstoreOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          { label: "Records", value: filteredData.length, color: "blue" },
          { label: "Qty", value: totals.QTY, color: "orange" },
          {
            label: "DAmount",
            value: totals.DPrice.toFixed(2),
            color: "amber",
          },
          {
            label: "CAmount",
            value: totals.CPrice.toFixed(2),
            color: "green",
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <SearchOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Group Name</div>
              <Input
                placeholder="Search Group Name"
                className="mainProductPicker"
                style={{ width: "12rem" }}
                allowClear
                suffix={<SearchOutlined style={{ color: "#1677ff" }} />}
                value={searchGroup}
                onChange={(e) => setSearchGroup(e.target.value)}
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <AppstoreOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Groups Found</div>
              <div className="mainProductValueBox">{data.length}</div>
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="GroupWiseStock"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No group wise stock records found"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell align="center">{totals.QTY}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.DPrice.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.CPrice.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }

  /* ── Group Name search input ── */
  .mainProductPicker.ant-input-affix-wrapper,
  .mainProductPicker.ant-input {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-input-affix-wrapper:hover,
  .mainProductPicker.ant-input:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-input-affix-wrapper-focused,
  .mainProductPicker.ant-input:focus {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker input.ant-input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
    background: transparent !important;
  }
  .mainProductPicker input.ant-input::placeholder {
    font-weight: 400;
    color: #a3a9b5;
  }

  /* ── Groups Found value box ── */
  .mainProductValueBox {
    display: flex;
    align-items: center;
    height: 34px;
    padding: 0 12px;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #d6e8ff;
    font-size: 14px;
    font-weight: 700;
    color: #1f2430;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
    </div>
  );
};

export default GropuWiseStock;